'use client'

import { useEffect, useState } from 'react'
import { Chevron } from './primitives'

type Option = { label: string }

const BUTTONS =
  ':scope > button, :scope > [role="tab"], :scope > [aria-pressed], :scope > [aria-selected]'

const isActive = (b: Element) =>
  b.getAttribute('aria-pressed') === 'true' ||
  b.getAttribute('aria-selected') === 'true' ||
  b.classList.contains('is-active') ||
  b.classList.contains('is-on')

// "01" + "Pilot audit" reads "01 · Pilot audit"; "All tools" + "13" reads
// "All tools (13)": the pill's own parts, joined for a plain option.
const labelOf = (b: Element) => {
  const parts = Array.from(b.childNodes)
    .map(n => (n.textContent ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
  if (parts.length > 1 && /^\d+$/.test(parts[0]))
    return `${parts[0]} · ${parts.slice(1).join(' ')}`
  if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1]))
    return `${parts.slice(0, -1).join(' ')} (${parts[parts.length - 1]})`
  return parts.join(' ')
}

/**
 * The phone dropdown for a row of pill tabs or filters (Lloyd, 2026-10-09):
 * below 641px a wrapped pill row read as broken, so the row (marked
 * `data-tabselect` in its markup, which hides it there from the first paint)
 * gives way to this select. It owns no behaviour: its options are the row's
 * buttons, choosing one clicks that button, so the page script or component
 * that drives the row keeps doing so, and it follows whichever button is
 * active (aria-pressed / aria-selected / is-active).
 */
export default function TabSelect({
  controls,
  label,
}: {
  /** id of the pill row this mirrors */
  controls: string
  /** accessible name, the row's own aria-label */
  label: string
}) {
  const [options, setOptions] = useState<Option[]>([])
  const [active, setActive] = useState(0)

  useEffect(() => {
    const row = document.getElementById(controls)
    if (!row) return
    const read = () => {
      const buttons = Array.from(row.querySelectorAll(BUTTONS))
      setOptions(buttons.map(b => ({ label: labelOf(b) })))
      setActive(Math.max(0, buttons.findIndex(isActive)))
    }
    read()
    const watch = new MutationObserver(read)
    watch.observe(row, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-pressed', 'aria-selected', 'class'],
    })
    return () => watch.disconnect()
  }, [controls])

  const choose = (i: number) => {
    const row = document.getElementById(controls)
    const button = row?.querySelectorAll<HTMLElement>(BUTTONS)[i]
    button?.click()
  }

  return (
    <label className='tabselect'>
      <select
        aria-label={label}
        value={options.length ? String(active) : ''}
        disabled={!options.length}
        onChange={e => choose(Number(e.target.value))}
      >
        {options.length ? (
          options.map((o, i) => (
            <option key={i} value={String(i)}>
              {o.label}
            </option>
          ))
        ) : (
          <option value=''>{label}</option>
        )}
      </select>
      <Chevron />
    </label>
  )
}
